<template>
  <div class="relative overflow-hidden rounded-[20px] bg-white pb-5 text-left text-sm leading-8">
    <div class="flip-container" :class="{ flipped: flipped }">
      <div
        class="flipper"
        :class="hoveredFront ? 'hovered-front' : hoveredBack ? 'hovered-back' : ''"
      >
        <div class="front">
          <div
            v-if="hasFacts"
            class="overlay"
            @click="flip"
            @mouseenter="hoveredFront = true"
            @mouseleave="hoveredFront = false"
          >
            <div class="overlay__triangle"></div>
            <arrow-down-icon class="overlay__arrow" />
            <div
              class="w-30 absolute -right-4 top-1/2 rotate-45 transform font-body text-base text-white"
              style="top: 38%"
            >
              Snabba fakta
            </div>
          </div>
          <nuxt-link :to="link">
            <div
              class="aspect-video overflow-hidden rounded-t-[20px] bg-blue-lightest object-cover"
            >
              <app-image
                :class="
                  containImage
                    ? 'h-full w-full object-contain object-center py-4'
                    : 'h-full w-full object-cover'
                "
                :url="imageUrl"
                :crop="containImage ? 'limit' : crop"
                :aspect="containImage ? undefined : '1.7778'"
                :gravity="containImage ? undefined : gravity"
                sizes="xs:100vw sm:100vw md:100vw lg:400px xl:560px 2xl:560px"
              ></app-image>
            </div>
          </nuxt-link>
        </div>
        <div v-if="hasFacts" class="back rounded-t-[20px]">
          <ul class="facts">
            <li
              v-for="fact in displayedFacts"
              :key="fact"
              class="flex items-start gap-[8px] pb-[10px]"
            >
              <span class="fact-dash text-white">-</span>
              <span class="fact-text text-white">{{ fact }}</span>
            </li>
          </ul>
          <nuxt-link :to="link">mer</nuxt-link>
          <div
            class="overlay"
            @click="flip"
            @mouseenter="hoveredBack = true"
            @mouseleave="hoveredBack = false"
          >
            <arrow-down-icon class="overlay__arrow" />
          </div>
        </div>
      </div>
    </div>
    <div class="z-3 flex flex-col justify-center px-6 pt-4">
      <div class="card__header__title">
        <app-button
          as="nuxt-link"
          :to="link"
          :variant="buttonVariant"
          position="right"
          arrow-type="double"
          size="desktop"
          class="!mx-auto !flex !w-full bg-white hover:bg-white"
        >
          {{ title }}
        </app-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Article } from '../types/contentful'
import { generateArticlePath } from '@/utils/slugResolver'
import { extractFactsFromSecondary } from '@/utils/contentfulTransformers'
import { fixImageUrlProtocol } from '@/utils/contentfulHelpers'
import ArrowDownIcon from './icons/ArrowDownIcon.vue'

interface Props {
  // For articles (recipes, klubbnytt, etc.)
  article?: Article
  // For animals — only the fields ContentCard actually uses
  animal?: {
    image: string
    kicker?: string
    species?: string
    title?: string
    'facts-block'?: { facts: string[] }
  }
  // Common props
  link?: string
  crop?: string
  gravity?: string
  containImage?: boolean // Use object-contain instead of object-cover for tall images
  buttonVariant?:
    | 'DEFAULT'
    | 'green'
    | 'purple'
    | 'blue'
    | 'peach'
    | 'pink'
    | 'rosa'
    | 'lila'
    | 'ljusrosa'
    | 'ljuslila'
}

const props = withDefaults(defineProps<Props>(), {
  article: undefined,
  animal: undefined,
  link: undefined,
  crop: 'fill',
  gravity: 'faces',
  containImage: false,
  buttonVariant: 'ljuslila',
})

// Reactive state for flip functionality
const flipped = ref(false)
const hoveredFront = ref(false)
const hoveredBack = ref(false)

// Computed properties
const isArticle = computed(() => !!props.article)

const imageUrl = computed(() => {
  if (isArticle.value && props.article && props.article?.fields?.cloudinaryImage) {
    return fixImageUrlProtocol(props.article.fields.cloudinaryImage[0]?.url)
  }
  if (props.animal) {
    return props.animal.image
  }
  return ''
})

const link = computed(() => {
  if (props.link) return props.link
  if (isArticle.value && props.article) {
    return generateArticlePath(props.article)
  }
  return '#'
})

const title = computed(() => {
  if (isArticle.value && props.article) {
    return props.article.fields.kicker || props.article.fields.title || ''
  }
  if (props.animal) {
    return props.animal.kicker || props.animal.species || props.animal.title || ''
  }
  return ''
})

const facts = computed(() => {
  // For animal articles
  if (props.animal && props.animal['facts-block']) {
    return props.animal['facts-block'].facts
  }

  // For articles - use the existing extractFactsFromSecondary function
  if (isArticle.value && props.article && props.article.includes?.Entry) {
    return extractFactsFromSecondary(props.article.fields.secondary || [], props.article.includes)
  }

  return []
})

const hasFacts = computed(() => {
  return facts.value.length > 0
})

const displayedFacts = computed(() => {
  if (!facts.value.length) return []
  return facts.value.slice(0, 8)
})

// Methods
const flip = () => {
  flipped.value = !flipped.value
}
</script>

<style lang="postcss" scoped>
/* Keep only essential styles for flip animation */

/* Custom Tailwind utilities */
.z-3 {
  z-index: 3;
}

.font-headline {
  font-family: theme('fontFamily.headline');
}

.font-body {
  font-family: theme('fontFamily.body');
}

.w-30 {
  width: 7.5rem;
}

.overlay {
  @apply z-10;

  position: absolute;
  top: 0;
  right: 0;
  height: 120px;
  width: 120px;
  cursor: pointer;
  transition: 0.3s;

  &:hover {
    .overlay__arrow {
      transform: rotate(180deg) scale(1.2);
    }
  }

  &__triangle {
    position: absolute;
    right: 0;
    top: 0;
    height: 0;
    width: 0;
    border-top: 60px solid theme('colors.green.dark');
    border-right: 60px solid theme('colors.green.dark');
    border-left: 60px solid transparent;
    border-bottom: 60px solid transparent;
    border-radius: 0 20px 0 0;
  }

  &__arrow {
    position: absolute;
    top: 10px;
    right: 10px;
    transform: rotate(0deg);
    width: 29.684px;
    height: 29.684px;
    transition: 0.5s;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
}

.text {
  line-height: 1.35;
}

.flip-container {
  perspective: 1000px;

  &.flipped .flipper {
    transform: rotateY(-180deg);
  }
}

.flip-container,
.front,
.back {
  width: 100%;
}

.flipper {
  position: relative;
  transform-style: preserve-3d;
  transition: 0.4s ease-out;

  @screen md {
    &.hovered-front {
      transition: 0.3s ease-out;
      transform: rotateY(-10deg);
    }

    .flip-container.flipped &.hovered-back {
      transition: 0.3s ease-out;
      transform: rotateY(-170deg);
    }
  }

  .flipped & {
    transition: 0.5s;
  }

  .front,
  .back {
    backface-visibility: hidden;
  }

  .front {
    transform: rotateY(0deg);
    z-index: 2;
  }

  .back {
    position: absolute;
    top: 0;
    left: 0;
    overflow: hidden;
    width: 100%;
    height: 100%;
    transform: rotateY(-180deg);
    background-color: theme('colors.green.dark');
    color: white;
    display: flex;
    flex-direction: column;
    align-items: center;

    .overlay {
      width: unset;
      height: unset;
    }

    .overlay__arrow {
      transform: rotate(-180deg);
      transition: transform 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
}

.facts {
  padding: 1.5rem;
  width: 100%;
  display: flex;
  flex-direction: column;

  li {
    @apply text-left;
    flex-shrink: 0;
  }

  .fact-dash {
    @apply text-sm font-normal leading-relaxed text-white;
    font-family: theme('fontFamily.headline-secondary');
    flex-shrink: 0;
    margin-top: 2px;
  }

  .fact-text {
    @apply text-sm font-normal leading-relaxed text-white;
    font-family: theme('fontFamily.headline-secondary');
    display: block;
    min-height: fit-content;
  }

  &::after {
    @apply pointer-events-none absolute bottom-0 left-0 h-[50%] w-full;
    content: '';
    background: linear-gradient(transparent, theme('colors.green.dark') 100%);
  }
}

.back {
  @apply text-center;
  a {
    @apply absolute bottom-0 z-10 py-3;
    @apply flex flex-col items-center bg-transparent py-2 outline-none;

    @apply mb-2 mt-8 rounded-full bg-white text-xs font-bold;

    padding: 0.3em 1em 0.2em;
    font-size: 1rem;
    color: theme('colors.green.dark');

    &:hover {
      transform: scale(1.05);
    }
  }
}

/* Content styles moved to Tailwind classes */

.share {
  font-size: 0.9rem;
  padding-bottom: 0.5rem;
  opacity: 0.65;

  &:hover {
    opacity: 1;
  }

  img {
    display: inline;
    max-width: 1.2rem;
  }
}
</style>
